import FormField from 'components/FormField';
import { ModalContent, ModalGrid, ModalHeader } from 'components/GlobalModal';
import KoalaIconButton from 'koala/components/IconButton';
import React, { useState } from 'react';
import KoalaButton from 'koala/components/Button';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setGlobalModalContent } from 'state/actions/globalUIActions';
import styled from 'styled-components';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryCache } from 'react-query';
import * as remoteApi from 'api/remote';
import { AxiosError } from 'axios';
import queryKeys from 'config/queryKeys';

const LinkInput = styled.div`
  grid-area: input;
  max-width: 80rem;
  margin: ${theme.spacing.x2} 0px;
  textarea {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
    font-size: 1.4rem;
    line-height: 1.4;
    padding: ${theme.spacing.x1};
    min-height: 10rem;
    width: 100%;
    border-radius: 4px;
    border: 1px solid #949494;
    resize: vertical;
    &:placeholder {
      color: #949494;
      font-size: 1.4rem;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.x1};
  align-items: center;
`;

function AddWebhook() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const translationKey = 'modals.addWebhook';
  const currentWorkspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);

  const [url, setUrl] = useState('');
  const queryCache = useQueryCache();

  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');

  // Outcomoe mutations
  const [testWebhookMutation, { isLoading: isTesting }] = useMutation(remoteApi.testWebhook, {
    onError: (e: AxiosError<{ message: string }>) => {
      setError('Error testing webhook');
      setIsValid(false);
    },
    onSuccess: () => {
      setError('');
      setIsValid(true);
    },
  });

  const [createWebhookMutation, { isLoading: isCreating }] = useMutation(remoteApi.createWebhook, {
    onError: (e: AxiosError<{ message: string }>) => {
      setError('Error creating webhook');
      setIsValid(false);
    },
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.webhooks);
      handleCancel();
    },
  });

  const handleAddWebhook = (e: any) => {
    e.preventDefault();
    setError('');

    const params = {
      workspaceId: currentWorkspace.slug,
      body: {
        url,
      },
    };

    createWebhookMutation(params);
  };

  const handleTestWebhook = async (e: any) => {
    e.preventDefault();
    setError('');

    const params = {
      workspaceId: currentWorkspace.slug,
      body: {
        url,
      },
    };

    testWebhookMutation(params);
  };

  const handleCancel = () => {
    dispatch(setGlobalModalContent(''));
  };

  return (
    <ModalGrid>
      <ModalHeader>
        <h2>{t(`${translationKey}.title`)}</h2>
        <KoalaIconButton onClick={handleCancel} iconName="close" />
      </ModalHeader>
      <ModalContent>
        <LinkInput>
          <form onSubmit={handleAddWebhook}>
            <FormField>
              <label>{t('modals.addWebhook.url')}</label>
              <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} />
            </FormField>
            <ButtonContainer>
              {!isValid && (
                <KoalaButton onClick={handleTestWebhook} appearance="subtle" disabled={isTesting} loading={isTesting}>
                  {t('modals.addWebhook.test')}
                </KoalaButton>
              )}
              {isValid && (
                <KoalaButton onClick={handleAddWebhook} disabled={isCreating} loading={isCreating}>
                  {t('shared.add')}
                </KoalaButton>
              )}
              <div style={{ color: 'red' }}>{error}</div>
              {isValid && <p style={{ color: 'green' }}>{t('modals.addWebhook.success')}</p>}
            </ButtonContainer>
          </form>
        </LinkInput>
      </ModalContent>
    </ModalGrid>
  );
}

export default AddWebhook;
